import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { LogOut, Maximize, Minimize, Zap, ZapOff } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';

export const UserMenu = styled.div`
  position: relative;
`;

export const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: rgba(20, 20, 30, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  overflow: hidden;
  z-index: 1000;
  padding: 8px;
`;

export const MenuButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: none;
  color: rgba(255,255,255,0.7);
  font-size: 13px;
  cursor: pointer;
  border-radius: 8px;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }
`;

// Status dot - green when connected, clickable to open settings
const StatusDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$status === 'error' ? '#f44336' : '#4CAF50'};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 0 6px ${props => props.$status === 'error' ? 'rgba(244, 67, 54, 0.6)' : 'rgba(76, 175, 80, 0.6)'};
  margin-left: 8px;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.3);
    box-shadow: 0 0 10px ${props => props.$status === 'error' ? 'rgba(244, 67, 54, 0.9)' : 'rgba(76, 175, 80, 0.9)'};
  }
`;

/** A screen's own entries in a header menu: [{ key, label, icon: LucideIcon, onClick }] */
export function MenuItems({ items, onPick }) {
  if (!items?.length) return null;
  return items.map(({ key, label, icon: Icon, onClick }) => (
    <MenuButton key={key} onClick={() => { onPick(); onClick(); }}>
      {Icon ? <Icon size={14} /> : null} {label}
    </MenuButton>
  ));
}

/**
 * The status dot (green / red) of a device that isn't signed in, and its menu: the screen's own
 * entries (`items`), full screen, low graphics and signing the device out (MainViewHeader).
 */
export default function HeaderDeviceMenu({ status = 'ok', onSettingsClick = null, items = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Closed by a press outside, caught on its way down: a map's pan-and-zoom stops a mousedown
  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [open]);

  // PIN-authed device sign-out (no Entra session to drop — the /screen flow
  // wrote its own localStorage keys which AuthGate doesn't see). Clear those
  // keys + bounce to /screen so the next user lands on the PIN field clean.
  const handleDeviceSignOut = () => {
    ['kuutio-instance', 'kuutio-participant-instance', 'kuutio-screen-admin',
     'kuutio-admin-auth', 'kuutio-device-locked', 'kuutio-admin-instance']
      .forEach(k => localStorage.removeItem(k));
    setOpen(false);
    window.location.href = '/screen';
  };

  // Fullscreen toggle for the workshop touchscreens — Samsung browser
  // address bar + tabs eat ~10% of screen real estate. Standard Fullscreen
  // API; vendor-prefix fallbacks for older WebView.
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== 'undefined' && Boolean(document.fullscreenElement)
  );
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(
      document.fullscreenElement || document.webkitFullscreenElement
    ));
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);
  const handleToggleFullscreen = async () => {
    setOpen(false);
    try {
      const el = document.documentElement;
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) await exit.call(document);
      } else {
        const req = el.requestFullscreen || el.webkitRequestFullscreen;
        if (req) await req.call(el);
      }
    } catch (err) {
      console.warn('Fullscreen toggle failed:', err);
    }
  };

  // Low-graphics toggle for older WebView / Samsung signage Chrome that
  // can't keep up with backdrop-filter + heavy compositing layers (GPU
  // surface budget overflow → scanline / VHS-static corruption band).
  // Mirrors the auto-detect in main.jsx via the same data-platform="tizen"
  // attribute on <html>; toggling it live re-applies the CSS overrides
  // without a reload.
  const [isLowGfx, setIsLowGfx] = useState(
    typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-platform') === 'tizen'
  );
  const handleToggleLowGfx = () => {
    setOpen(false);
    const next = !isLowGfx;
    try {
      if (next) {
        window.localStorage.setItem('kuutio-lowgfx', '1');
        document.documentElement.setAttribute('data-platform', 'tizen');
      } else {
        window.localStorage.removeItem('kuutio-lowgfx');
        document.documentElement.removeAttribute('data-platform');
      }
    } catch (err) {
      console.warn('LowGfx toggle failed:', err);
    }
    setIsLowGfx(next);
  };

  return (
    <UserMenu ref={menuRef}>
      <StatusDot
        $status={status}
        onClick={() => setOpen(prev => !prev)}
        title={t('navigation.userSettings')}
      />
      {open && (
        <UserDropdown>
          {onSettingsClick && (
            <MenuButton onClick={() => { setOpen(false); onSettingsClick(); }}>
              Asetukset
            </MenuButton>
          )}
          <MenuItems items={items} onPick={() => setOpen(false)} />
          <MenuButton onClick={handleToggleFullscreen}>
            {isFullscreen
              ? <><Minimize size={14} /> Poistu kokoruututilasta</>
              : <><Maximize size={14} /> Kokoruututila</>}
          </MenuButton>
          <MenuButton onClick={handleToggleLowGfx}>
            {isLowGfx
              ? <><Zap size={14} /> Palauta grafiikka</>
              : <><ZapOff size={14} /> Hidasta grafiikkaa</>}
          </MenuButton>
          <MenuButton onClick={handleDeviceSignOut}>
            <LogOut size={14} /> {t('navigation.signOut')}
          </MenuButton>
        </UserDropdown>
      )}
    </UserMenu>
  );
}
